import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import { 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  subWeeks, 
  subMonths 
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getTodayInOfficeTimezone, formatDateInOfficeTimezone } from '@/lib/timezoneUtils';
import { OFFICE_TIMEZONE } from '@/locales';

export type AgingBucket = 'all' | '1-30' | '31-60' | '61-90' | '90+';

export type PeriodFilter = 
  | 'all' 
  | 'today' 
  | 'yesterday' 
  | 'this_week' 
  | 'last_week' 
  | 'this_month' 
  | 'last_month'
  | 'due_today'
  | 'due_this_week';

export interface OverduePayment {
  payment_id: string;
  deal_id: string;
  contact_id: string;
  cliente: string;
  primary_phone: string | null;
  primary_phone_country_code: string | null;
  description: string;
  amount: number;
  paid_amount: number;
  valor_aberto: number;
  due_date: string;
  dias_atraso: number;
  faixa: string;
  status: string;
  collection_status: string | null;
  vendedor: string;
  vendedor_id: string | null;
  business_unit: string;
  brand: string;
  currency: string;
}

interface CollectionSummary {
  totalOverdue: number;
  totalClients: number;
  maxDaysOverdue: number;
  byBucket: {
    '1-30': { count: number; amount: number };
    '31-60': { count: number; amount: number };
    '61-90': { count: number; amount: number };
    '90+': { count: number; amount: number };
  };
}

interface UseCollectionDashboardOptions {
  businessUnit?: BusinessUnit;
  agingBucket?: AgingBucket;
  vendedorId?: string;
  period?: PeriodFilter;
}

/**
 * Calcula as datas de início e fim para cada período
 * Usa timezone de Orlando (America/New_York) para lógica de negócio
 * Semana começa na SEGUNDA-FEIRA (weekStartsOn: 1)
 */
function getPeriodDateRange(period: PeriodFilter): { startDate: string; endDate: string } | null {
  // Use Orlando timezone for business date calculations
  const todayInOrlando = getTodayInOfficeTimezone();
  const formatDate = (date: Date) => formatDateInOfficeTimezone(date);
  const formatTz = (date: Date) => formatInTimeZone(date, OFFICE_TIMEZONE, 'yyyy-MM-dd');

  switch (period) {
    case 'all':
      return null;
    
    case 'today': {
      const todayStr = formatDate(todayInOrlando);
      return { startDate: todayStr, endDate: todayStr };
    }
    
    case 'yesterday': {
      const yesterday = subDays(todayInOrlando, 1);
      const yesterdayStr = formatDate(yesterday);
      return { startDate: yesterdayStr, endDate: yesterdayStr };
    }
    
    case 'this_week':
      return {
        startDate: formatTz(startOfWeek(todayInOrlando, { weekStartsOn: 1 })),
        endDate: formatTz(endOfWeek(todayInOrlando, { weekStartsOn: 1 })),
      };
    
    case 'last_week': {
      const lastWeek = subWeeks(todayInOrlando, 1);
      return {
        startDate: formatTz(startOfWeek(lastWeek, { weekStartsOn: 1 })),
        endDate: formatTz(endOfWeek(lastWeek, { weekStartsOn: 1 })),
      };
    }
    
    case 'this_month':
      return {
        startDate: formatTz(startOfMonth(todayInOrlando)),
        endDate: formatTz(endOfMonth(todayInOrlando)),
      };
    
    case 'last_month': {
      const lastMonth = subMonths(todayInOrlando, 1);
      return {
        startDate: formatTz(startOfMonth(lastMonth)),
        endDate: formatTz(endOfMonth(lastMonth)),
      };
    }
    
    case 'due_today': {
      const todayStr = formatDate(todayInOrlando);
      return { startDate: todayStr, endDate: todayStr };
    }
    
    case 'due_this_week':
      return {
        startDate: formatDate(todayInOrlando),
        endDate: formatTz(endOfWeek(todayInOrlando, { weekStartsOn: 1 })),
      };
    
    default:
      return null;
  }
}

export function useCollectionDashboard({
  businessUnit = 'all',
  agingBucket = 'all',
  vendedorId,
  period = 'all',
}: UseCollectionDashboardOptions = {}) {
  return useQuery({
    queryKey: ['collection-dashboard', businessUnit, agingBucket, vendedorId, period],
    queryFn: async () => {
      // Para "due_today" e "due_this_week", não filtramos por dias_atraso > 0
      const isDuePeriod = period === 'due_today' || period === 'due_this_week';
      
      let query = supabase
        .from('v_payments_aging_detail')
        .select('*');
      
      // Se não for período de "vencendo", filtra apenas em atraso
      if (!isDuePeriod) {
        query = query.gt('dias_atraso', 0);
      }
      
      query = query.order('dias_atraso', { ascending: false });

      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }

      if (vendedorId) {
        query = query.eq('vendedor_id', vendedorId);
      }

      // Filtro de período por due_date
      const dateRange = getPeriodDateRange(period);
      if (dateRange) {
        query = query.gte('due_date', dateRange.startDate).lte('due_date', dateRange.endDate);
      }

      // Filter by aging bucket
      if (agingBucket !== 'all') {
        switch (agingBucket) {
          case '1-30':
            query = query.gte('dias_atraso', 1).lte('dias_atraso', 30);
            break;
          case '31-60':
            query = query.gte('dias_atraso', 31).lte('dias_atraso', 60);
            break;
          case '61-90':
            query = query.gte('dias_atraso', 61).lte('dias_atraso', 90);
            break;
          case '90+':
            query = query.gt('dias_atraso', 90);
            break;
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      const payments = (data || []) as OverduePayment[];

      // Calculate summary
      const uniqueContacts = new Set(payments.map(p => p.contact_id));
      const summary: CollectionSummary = {
        totalOverdue: payments.reduce((sum, p) => sum + (p.valor_aberto || 0), 0),
        totalClients: uniqueContacts.size,
        maxDaysOverdue: payments.length > 0 ? Math.max(...payments.map(p => p.dias_atraso)) : 0,
        byBucket: {
          '1-30': { count: 0, amount: 0 },
          '31-60': { count: 0, amount: 0 },
          '61-90': { count: 0, amount: 0 },
          '90+': { count: 0, amount: 0 },
        },
      };

      // Group by bucket
      payments.forEach(p => {
        if (p.dias_atraso <= 30) {
          summary.byBucket['1-30'].count++;
          summary.byBucket['1-30'].amount += p.valor_aberto || 0;
        } else if (p.dias_atraso <= 60) {
          summary.byBucket['31-60'].count++;
          summary.byBucket['31-60'].amount += p.valor_aberto || 0;
        } else if (p.dias_atraso <= 90) {
          summary.byBucket['61-90'].count++;
          summary.byBucket['61-90'].amount += p.valor_aberto || 0;
        } else {
          summary.byBucket['90+'].count++;
          summary.byBucket['90+'].amount += p.valor_aberto || 0;
        }
      });

      return { payments, summary };
    },
  });
}

export function useVendedoresList() {
  return useQuery({
    queryKey: ['vendedores-with-overdue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_payments_aging_detail')
        .select('vendedor, vendedor_id')
        .gt('dias_atraso', 0)
        .not('vendedor', 'is', null);

      if (error) throw error;

      // Get unique vendedores
      const uniqueVendedores = new Map<string, { id: string; name: string }>();
      (data || []).forEach(row => {
        if (row.vendedor_id && row.vendedor) {
          uniqueVendedores.set(row.vendedor_id, {
            id: row.vendedor_id,
            name: row.vendedor,
          });
        }
      });

      return Array.from(uniqueVendedores.values()).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
    },
  });
}
